'use client';

import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocale } from './LocaleProvider';
import { SophiaLogo } from './SophiaLogo';

/**
 * ScrollPinnedHero — the 5-beat signature moment.
 *
 * Apple-Vision-pro-page style: ~500vh tall, a sticky viewport shows
 * a single canvas that transforms through 5 beats as the user scrolls:
 *
 *  Beat 1 [0–20%]   Editorial serif headline centers, grain visible
 *  Beat 2 [20–40%]  Mock product page slides in from right
 *  Beat 3 [40–60%]  Sophia pill appears bottom-right, pulses
 *  Beat 4 [60–80%]  ResultsModal draws in from left, data animates in
 *  Beat 5 [80–100%] Resale chart traces, alt-price chips cascade
 *
 * All motion is scroll-linked (not time-based) so scrubbing back and
 * forth is perfectly reversible. GSAP ScrollTrigger pins the viewport,
 * Lenis (in SmoothScroll) keeps the scrub smooth.
 *
 * Honors prefers-reduced-motion: collapses to a static final-frame view.
 */
export function ScrollPinnedHero() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';

  const root = useRef<HTMLElement>(null);
  const pinEl = useRef<HTMLDivElement>(null);

  // Refs for each animated element
  const headline = useRef<HTMLDivElement>(null);
  const subheadline = useRef<HTMLDivElement>(null);
  const productCard = useRef<HTMLDivElement>(null);
  const sophiaPill = useRef<HTMLDivElement>(null);
  const modalCard = useRef<HTMLDivElement>(null);
  const modalPrice = useRef<HTMLDivElement>(null);
  const modalAlt = useRef<HTMLDivElement>(null);
  const modalResale = useRef<HTMLDivElement>(null);
  const modalChart = useRef<SVGPathElement>(null);
  const scrollHint = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (typeof window === 'undefined') return;
      gsap.registerPlugin(ScrollTrigger);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // Static end-state for reduced motion users
        gsap.set([headline.current, subheadline.current, productCard.current,
          sophiaPill.current, modalCard.current], { opacity: 1, x: 0, y: 0, scale: 1 });
        return;
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: '+=500%',
          pin: pinEl.current,
          scrub: 1.2,
          anticipatePin: 1,
        },
      });

      // Beat 1 → 2: headline scales down, product page slides in
      tl.to(headline.current, { scale: 0.82, y: -40, opacity: 0.3 }, 0.8)
        .to(subheadline.current, { opacity: 0, y: -20 }, 0.7)
        .fromTo(
          productCard.current,
          { xPercent: isAr ? -120 : 120, opacity: 0, rotate: isAr ? -4 : 4 },
          { xPercent: 0, opacity: 1, rotate: 0, ease: 'power3.out' },
          1,
        )
        .to(scrollHint.current, { opacity: 0, y: 20 }, 0.3);

      // Beat 3: Sophia pill appears
      tl.fromTo(
        sophiaPill.current,
        { scale: 0.4, opacity: 0 },
        { scale: 1, opacity: 1, ease: 'back.out(2.2)' },
        2.2,
      ).to(
        sophiaPill.current,
        { scale: 1.08, yoyo: true, repeat: 1, duration: 0.3 },
        2.8,
      );

      // Beat 4: ResultsModal draws in from left with content staggered
      tl.fromTo(
        modalCard.current,
        { xPercent: isAr ? 110 : -110, opacity: 0, scale: 0.94 },
        { xPercent: 0, opacity: 1, scale: 1, ease: 'power3.out' },
        3.2,
      );

      tl.fromTo(modalPrice.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0 }, 3.6);
      tl.fromTo(modalAlt.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0 }, 3.8);
      tl.fromTo(modalResale.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0 }, 4.0);

      // Beat 5: chart traces + alt chips cascade (handled via modalAlt children)
      if (modalChart.current) {
        const len = modalChart.current.getTotalLength();
        gsap.set(modalChart.current, { strokeDasharray: len, strokeDashoffset: len });
        tl.to(modalChart.current, { strokeDashoffset: 0, ease: 'power2.out' }, 4.2);
      }

      // Final: whole composition drifts up as we exit
      tl.to([productCard.current, modalCard.current, sophiaPill.current],
        { y: -30, opacity: 0.8 }, 4.8);
    },
    { scope: root, dependencies: [isAr] },
  );

  const copy = isAr
    ? {
        eyebrow: 'لمتسوّق الفخامة في الخليج',
        headline1: 'اعرف',
        headline2: 'السعر الحقيقي',
        headline3: 'للفخامة.',
        sub: '42+ متجرًا فاخرًا، قيمة إعادة بيع فورية، فحص أصالة. بالعربية.',
        scroll: 'مرّر',
        pill: 'هل أشتري؟',
        product: 'Gucci GG Marmont Small',
        priceLabel: 'السعر الحالي',
        savingsBadge: 'وفّر AED 2,400',
        altLabel: 'أفضل سعر',
        resaleLabel: 'قيمة إعادة البيع',
        modalAt: 'The Luxury Closet',
        typical: 'سعر نموذجي',
      }
    : {
        eyebrow: 'For the luxury shopper in the GCC',
        headline1: 'Know the',
        headline2: 'real price',
        headline3: 'of luxury.',
        sub: '42+ designer retailers, live resale estimates, authenticity scoring. Arabic and English.',
        scroll: 'Scroll',
        pill: 'Should I Buy This?',
        product: 'Gucci GG Marmont Small',
        priceLabel: 'CURRENT',
        savingsBadge: 'Save AED 2,400',
        altLabel: 'BETTER PRICE',
        resaleLabel: 'RESALE VALUE',
        modalAt: 'The Luxury Closet',
        typical: 'TYPICAL',
      };

  return (
    <section
      ref={root}
      // Pinned-scrub animation is a DESKTOP-ONLY experience. Mobile
      // users see MobileHero (a stacked static hero) instead; this
      // section is display: none below md.
      //
      // motion-reduce:md:h-screen collapses the 6-screen scroll-jail
      // to a single viewport for users with prefers-reduced-motion.
      // The GSAP init block already paints the final frame for them.
      className="relative hidden md:block md:h-[600vh] motion-reduce:md:h-screen"
      aria-label="Sophia in 5 scrolls"
    >
      <div
        ref={pinEl}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* Ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-radial-gold"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 w-[60rem] h-[60rem] rounded-full bg-cta-from/10 blur-[140px]"
        />

        {/* Beat 1 — headline (centered *below* the fixed nav).
            pt-24 pushes the centered content down so the eyebrow pill
            sits below the nav's ~72px chrome, not behind it. */}
        <div
          ref={headline}
          className="absolute inset-0 pt-24 flex flex-col items-center justify-center px-6 text-center will-change-transform"
        >
          <div
            ref={subheadline}
            className="mb-8 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold-soft text-xs tracking-[0.25em] uppercase"
          >
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {copy.eyebrow}
          </div>
          <h1
            className="font-serif text-display leading-[1.05] sm:leading-[0.98] md:leading-[0.92] max-w-[14ch] px-2"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'wght' 400" }}
          >
            <span className="block text-cream">{copy.headline1}</span>
            <span className="block shimmer-gold italic">{copy.headline2}</span>
            <span className="block text-cream">{copy.headline3}</span>
          </h1>
          <p className="mt-6 sm:mt-10 max-w-xl text-fog-300 text-base sm:text-lg md:text-xl leading-relaxed px-4 sm:px-0">
            {copy.sub}
          </p>

          <div
            ref={scrollHint}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 text-fog-500 text-xs tracking-[0.3em] uppercase flex flex-col items-center gap-3"
          >
            <span>{copy.scroll}</span>
            <span className="w-px h-10 bg-gradient-to-b from-gold/60 to-transparent animate-pulse" />
          </div>
        </div>

        {/*
         * Beat 2 — mock product page.
         *  - Desktop: positioned right-center, 44vw wide
         *  - Mobile: stacked upper area, full-width (88vw) and shorter
         *    vertical offset so it doesn't overlap the modal card
         */}
        <div
          ref={productCard}
          className="absolute w-[min(92vw,420px)] md:w-[min(44vw,580px)]
                     top-[6%] md:top-1/2 md:-translate-y-1/2
                     start-1/2 -translate-x-1/2 md:start-auto md:-translate-x-0 md:end-[6vw]
                     rtl:md:start-[6vw] rtl:md:end-auto
                     opacity-0 will-change-transform"
          dir="ltr"
        >
          <div className="rounded-2xl bg-obsidian-700 border border-white/10 shadow-card-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-obsidian-800 border-b border-white/5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              <div className="ml-4 flex-1 text-[11px] text-fog-500 font-mono truncate">
                farfetch.com/shopping/gucci-gg-marmont
              </div>
            </div>
            <div className="p-6 pt-5 min-h-[340px] bg-gradient-to-b from-obsidian-700 to-obsidian-800">
              <div className="flex gap-5">
                <div className="w-[140px] h-[180px] rounded-lg overflow-hidden border border-white/5 bg-obsidian-800">
                  {/* Product image: Gucci GG Marmont Small Black via The Luxury Closet */}
                  <img
                    src="/images/hero-bag.jpg"
                    alt="Gucci GG Marmont Small black matelassé leather shoulder bag"
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                  />
                </div>
                <div className="flex-1 space-y-2 pt-1">
                  <div className="text-[10px] tracking-[0.2em] text-fog-500 uppercase">
                    Gucci
                  </div>
                  <div className="text-cream text-sm leading-snug">{copy.product}</div>
                  <div className="text-cream text-sm font-medium">AED 9,400</div>
                  <div className="h-6" />
                  <div className="h-2 bg-white/5 rounded w-3/4" />
                  <div className="h-2 bg-white/5 rounded w-1/2" />
                  <div className="h-2 bg-white/5 rounded w-5/6" />
                </div>
              </div>

              {/* Beat 3 — Sophia pill */}
              <div
                ref={sophiaPill}
                className="absolute bottom-6 end-6 rtl:start-6 rtl:end-auto rounded-pill bg-cta-gradient text-white text-xs font-semibold px-4 py-2.5 shadow-glow flex items-center gap-1.5 opacity-0 will-change-transform"
                data-cursor="cta"
              >
                <SophiaLogo variant="mono" size={16} />
                <span>{copy.pill}</span>
              </div>
            </div>
          </div>
        </div>

        {/*
         * Beat 4 — ResultsModal.
         *  - Desktop: positioned left-center, 38vw wide
         *  - Mobile: stacked lower area, 88vw wide; sits under the product
         *    card so the user reads top-down instead of left-right
         */}
        <div
          ref={modalCard}
          className="absolute w-[min(92vw,420px)] md:w-[min(38vw,480px)]
                     bottom-[6%] md:bottom-auto md:top-1/2 md:-translate-y-1/2
                     start-1/2 -translate-x-1/2 md:start-[6vw] md:-translate-x-0
                     rtl:md:start-auto rtl:md:end-[6vw]
                     opacity-0 will-change-transform"
          dir="ltr"
        >
          <div className="rounded-2xl bg-cream text-obsidian-900 p-5 shadow-card-lg">
            <div className="flex items-center gap-2 mb-4">
              <SophiaLogo size={28} className="rounded-md" />
              <span className="text-sm font-semibold">Sophia</span>
              <span className="ml-auto text-[9px] tracking-widest text-gold-deep font-bold px-1.5 py-0.5 bg-gold/15 rounded">
                {copy.typical}
              </span>
            </div>

            <div ref={modalPrice} className="mb-4">
              <div className="text-[10px] tracking-widest uppercase text-fog-700 mb-1">
                {copy.priceLabel}
              </div>
              <div className="font-serif text-3xl font-bold leading-none">AED 9,400</div>
            </div>

            <div
              ref={modalAlt}
              className="rounded-luxe bg-gradient-to-br from-violet-50 to-indigo-50 p-4 mb-3 border border-violet-100"
            >
              <div className="text-[10px] tracking-widest uppercase text-violet-800 font-semibold mb-1">
                {copy.altLabel}
              </div>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs text-obsidian-800">{copy.modalAt}</div>
                  <div className="font-serif text-2xl font-bold text-violet-800">AED 7,000</div>
                </div>
                <div className="text-[10px] text-emerald font-semibold whitespace-nowrap">
                  {copy.savingsBadge}
                </div>
              </div>
            </div>

            <div ref={modalResale} className="rounded-luxe bg-obsidian-900/5 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-widest uppercase text-fog-700 font-semibold">
                  {copy.resaleLabel}
                </div>
                <div className="text-[10px] font-mono text-gold-deep">45%</div>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-serif text-xl font-bold">AED 4,200</div>
              </div>
              {/* Traced resale chart */}
              <svg
                viewBox="0 0 180 40"
                className="w-full h-10"
                preserveAspectRatio="none"
              >
                <path
                  ref={modalChart}
                  d="M 2 34 L 24 30 L 46 26 L 68 28 L 90 22 L 112 18 L 134 20 L 156 14 L 178 10"
                  fill="none"
                  stroke="#c9a96a"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c9a96a" stopOpacity="0.22" />
                    <stop offset="100%" stopColor="#c9a96a" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
